import { clsx } from "clsx";

interface ImageSlotProps {
  /** Public-path image src. When omitted, a styled placeholder block is shown with the hint. */
  src?: string;
  alt?: string;
  /** Editorial hint describing the intended photo (shown faintly when no src is set). */
  placeholder?: string;
  /** data-px parallax depth (consumed by the home motion hook). */
  parallax?: number;
  /** Border radius in px (the design uses a few specific values). */
  radius?: number;
  /** Positioning / sizing classes supplied by the caller (e.g. absolute inset offsets). */
  className?: string;
}

/**
 * Recreation of the Claude Design `<image-slot>` primitive. Renders the photo when available,
 * otherwise a dark placeholder card (#17130f) carrying the editorial hint — so the layout is
 * pixel-faithful and it's obvious which real photo each slot expects.
 */
export function ImageSlot({ src, alt = "", placeholder, parallax, radius, className }: ImageSlotProps) {
  return (
    <div
      data-px={parallax}
      style={radius ? { borderRadius: `${radius}px` } : undefined}
      className={clsx("overflow-hidden bg-[#17130f]", className)}
    >
      {src ? (
        <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#1a1410] to-[#100c09] p-6">
          {placeholder && placeholder.trim() ? (
            <span className="max-w-[26ch] text-center font-mono text-[11px] uppercase leading-relaxed tracking-[.12em] text-white/25">
              {placeholder}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
